import {
  useMemo,
  createContext,
  useContext,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface StepDefinition {
  id: string;
  title: string;
  component: ReactNode;
  /** Validation function to run before leaving this step */
  validate?: () => boolean | Promise<boolean>;
}

interface StepWizardContextValue {
  currentStep: number;
  totalSteps: number;
  steps: StepDefinition[];
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  goToStep: (index: number) => Promise<void>;
  complete: () => Promise<void>;
  isFirstStep: boolean;
  isLastStep: boolean;
  onCancel?: () => void;
  onComplete?: () => Promise<void>;
  canProceed: boolean;
  nextLabel?: string;
  completeLabel?: string;
  secondaryLabel?: string;
  stickyHeader?: boolean;
  isLoading?: boolean;
  isValidating?: boolean;
  /**
   * Internal in-flight flag the wizard sets itself while an async `validate()` /
   * `onComplete()` runs. Consumers can't drive this — the wizard owns that async
   * window — so it's folded into the CTA disabled state to block double-submits.
   */
  isBusy?: boolean;
  /** Primary (next/complete) CTA button ref, so a step can anchor a popover to it. */
  primaryActionRef?: RefObject<HTMLButtonElement | null>;
  /** Secondary (save-and-exit / cancel) CTA button ref, so a step can anchor a popover to it. */
  secondaryActionRef?: RefObject<HTMLButtonElement | null>;
}

interface StepWizardProps {
  steps: StepDefinition[];
  currentStep: number;
  onStepChange: (nextStep: number) => void;
  onCancel?: () => void;
  onComplete?: () => Promise<void>;
  isLoading?: boolean;
  isValidating?: boolean;
  canProceed?: boolean;
  nextLabel?: string;
  completeLabel?: string;
  /**
   * Label for the ghost (onCancel) button. Defaults to `common.cancel` so existing
   * consumers (webhooks) are unaffected; campaigns passes `campaigns.save_and_exit`.
   */
  secondaryLabel?: string;
  /**
   * When true, pins the step-indicator + button row to the top of the scroll
   * container so the actions never scroll out of view. Default off (no regression).
   */
  stickyHeader?: boolean;
  className?: string;
  children: ReactNode;
}

const StepWizardContext = createContext<StepWizardContextValue | undefined>(undefined);

/**
 * Hook to access StepWizard state and actions.
 * Throws if used outside of <StepWizard>.
 *
 * @returns {StepWizardContextValue} The current wizard state and actions.
 * @example
 * const { currentStep, goToNext, goToPrevious } = useStepWizard();
 */
export function useStepWizard(): StepWizardContextValue {
  const ctx = useContext(StepWizardContext);
  if (!ctx) {
    throw new Error('useStepWizard must be used within StepWizard');
  }
  return ctx;
}

const StepWizardHeader = () => {
  const { t } = useTranslation();
  const context = useContext(StepWizardContext);
  if (!context) {
    return null;
  }

  const {
    goToNext,
    goToPrevious,
    complete,
    onCancel,
    isLoading,
    isValidating,
    isBusy,
    isFirstStep,
    isLastStep,
    canProceed,
    nextLabel,
    completeLabel,
    secondaryLabel,
    primaryActionRef,
    secondaryActionRef,
  } = context;

  const handleNext = async () => {
    if (isLastStep) {
      await complete();
    } else {
      await goToNext();
    }
  };

  // `isBusy` covers the wizard's own in-flight validate/complete window so a rapid
  // double-click can't fire the step's async save twice (e.g. campaign senders).
  const isNavigationDisabled = isLoading || isValidating || isBusy;
  const isPrimaryCtaDisabled = isNavigationDisabled || !canProceed;

  return (
    <div className="flex items-center gap-2 p-0.5">
      <Button
        ref={secondaryActionRef}
        variant="ghost-neutral"
        onClick={onCancel}
        disabled={isNavigationDisabled || !onCancel}
      >
        {secondaryLabel ?? t('common.cancel')}
      </Button>
      {!isFirstStep && (
        <Button variant="outline-neutral" onClick={goToPrevious} disabled={isNavigationDisabled}>
          {t('common.previous')}
        </Button>
      )}
      <Button ref={primaryActionRef} onClick={handleNext} disabled={isPrimaryCtaDisabled}>
        {isLastStep ? (completeLabel ?? t('common.create')) : (nextLabel ?? t('common.continue'))}
      </Button>
    </div>
  );
};

/**
 * Container that renders the wizard header and the currently active step.
 * This component must be used inside <StepWizard>.
 */
const StepWizardSteps = () => {
  const { t } = useTranslation();
  const context = useContext(StepWizardContext);
  if (!context) {
    throw new Error('StepWizard.Steps must be used within StepWizard');
  }

  const { currentStep, totalSteps, steps, stickyHeader } = context;

  const currentStepData = steps[currentStep];

  const previousStepRef = useRef(currentStep);
  const isFirstRenderRef = useRef(true);
  const direction = currentStep >= previousStepRef.current ? 'forward' : 'backward';

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }

    previousStepRef.current = currentStep;
  }, [currentStep]);

  const transitionClassName = useMemo(() => {
    const base = 'duration-300 motion-reduce:animate-none motion-reduce:transform-none';

    if (isFirstRenderRef.current) {
      return cn(base, 'animate-in fade-in-0');
    }

    const slideClass =
      direction === 'backward'
        ? 'animate-in fade-in-0 slide-in-from-left-6'
        : 'animate-in fade-in-0 slide-in-from-right-6';

    return cn(base, slideClass);
  }, [direction]);

  return (
    <div
      role="group"
      aria-label={`Step ${currentStep + 1} of ${totalSteps}: ${currentStepData.title}`}
    >
      <div
        className={cn(
          'flex items-center justify-between pl-1.5',
          // Sticky pins the indicator + buttons to the scroll container's top so the
          // actions never scroll out of view; off by default keeps existing layout.
          stickyHeader ? 'sticky top-0 z-10 bg-subtle py-4' : 'mb-6'
        )}
      >
        <div>
          <p className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
            {t('campaigns.step_indicator', { 0: currentStep + 1, 1: totalSteps })}
          </p>
          <h2 className="mt-2 text-xl leading-none font-semibold">{currentStepData.title}</h2>
        </div>
        <StepWizardHeader />
      </div>
      {/* key remounts the body on each step change: the slide animation depends on it,
          and it intentionally mirrors the old per-route lifecycle so each step's
          mount-time side effects (pageView, useTitle, schedule-restore) re-fire. */}
      <div
        className={cn('p-0.5', transitionClassName)}
        aria-current="step"
        key={currentStepData.id}
      >
        {currentStepData.component}
      </div>
    </div>
  );
};

/**
 * StepWizard - Fully controlled multi-step wizard component
 *
 * All state is managed by the parent component. The parent must:
 * 1. Provide the array of steps with their content
 * 2. Track the current step index
 * 3. Handle step changes (including validation)
 * 4. Manage loading/validation states
 *
 * @example
 * const [currentStep, setCurrentStep] = useState(0);
 * const [isValid, setIsValid] = useState(false);
 *
 * const steps: StepDefinition[] = [
 *   { id: 'profile', title: 'Profile', component: <ProfileForm /> },
 *   { id: 'settings', title: 'Settings', component: <SettingsForm /> },
 *   { id: 'confirm', title: 'Confirm', component: <ConfirmStep /> }
 * ];
 *
 * const handleStepChange = async (nextStep: number) => {
 *   // Perform validation if needed
 *   const isValid = await validateCurrentStep();
 *   if (isValid) {
 *     setCurrentStep(nextStep);
 *   }
 * };
 *
 * <StepWizard
 *   steps={steps}
 *   currentStep={currentStep}
 *   canProceed={isValid}
 *   onStepChange={handleStepChange}
 *   onComplete={handleComplete}
 * >
 *   <StepWizard.Steps />
 * </StepWizard>
 */
export const StepWizard = ({
  steps,
  currentStep,
  onStepChange,
  onCancel,
  onComplete,
  isLoading = false,
  isValidating = false,
  canProceed = true,
  nextLabel,
  completeLabel,
  secondaryLabel,
  stickyHeader = false,
  className,
  children,
}: StepWizardProps) => {
  const totalSteps = steps.length;

  // In-flight lock for the wizard's own async transitions. The wizard runs each
  // step's `validate()` (and `onComplete()`) internally, so a consumer can't disable
  // the CTAs for that window from outside — without this, a rapid double-click on
  // Continue runs `validate()` twice, double-firing the step's save (on the campaign
  // messaging step that means `replaceCampaignSendersSequentially` runs twice). The
  // ref blocks re-entry synchronously (before React re-renders); the state drives the
  // visible disabled style on the buttons.
  const navigationLockRef = useRef(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const secondaryActionRef = useRef<HTMLButtonElement>(null);

  const runGuarded = useCallback(async (action: () => Promise<void>) => {
    if (navigationLockRef.current) {
      return;
    }
    navigationLockRef.current = true;
    setIsNavigating(true);
    try {
      await action();
    } finally {
      navigationLockRef.current = false;
      setIsNavigating(false);
    }
  }, []);

  const contextValue = useMemo(() => {
    const runValidation = async (targetStep: number) => {
      const currentDefinition = steps[currentStep];

      if (!currentDefinition?.validate) {
        return true;
      }

      const isMovingForward = targetStep > currentStep;

      if (!isMovingForward && targetStep !== currentStep) {
        return true;
      }

      try {
        return (await currentDefinition.validate()) ?? true;
      } catch {
        return false;
      }
    };

    const goToNext = () =>
      runGuarded(async () => {
        const nextStep = Math.min(currentStep + 1, totalSteps - 1);
        if (nextStep === currentStep) {
          return;
        }

        const canLeave = await runValidation(nextStep);
        if (!canLeave) {
          return;
        }

        onStepChange(nextStep);
      });

    const goToPrevious = () => {
      const prevStep = Math.max(currentStep - 1, 0);
      if (prevStep === currentStep) {
        return;
      }

      onStepChange(prevStep);
    };

    const goToStep = (index: number) =>
      runGuarded(async () => {
        if (index < 0 || index >= totalSteps || index === currentStep) {
          return;
        }

        const isForward = index > currentStep;

        if (isForward) {
          const canLeave = await runValidation(index);
          if (!canLeave) {
            return;
          }
        }

        onStepChange(index);
      });

    const complete = () =>
      runGuarded(async () => {
        const isValid = await runValidation(currentStep);
        if (!isValid) {
          return;
        }

        await onComplete?.();
      });

    return {
      currentStep,
      totalSteps,
      steps,
      goToNext,
      goToPrevious,
      goToStep,
      complete,
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === totalSteps - 1,
      onCancel,
      onComplete,
      canProceed,
      nextLabel,
      completeLabel,
      secondaryLabel,
      stickyHeader,
      isLoading,
      isValidating,
      isBusy: isNavigating,
      primaryActionRef,
      secondaryActionRef,
    };
  }, [
    currentStep,
    totalSteps,
    steps,
    onStepChange,
    onCancel,
    onComplete,
    canProceed,
    nextLabel,
    completeLabel,
    secondaryLabel,
    stickyHeader,
    isLoading,
    isValidating,
    runGuarded,
    isNavigating,
  ]);

  return (
    <StepWizardContext.Provider value={contextValue as StepWizardContextValue}>
      <div className={cn('mx-auto max-w-7xl', className)}>{children}</div>
    </StepWizardContext.Provider>
  );
};

StepWizard.Steps = StepWizardSteps;
